import "./PoiDetail.css";

import { Controller } from "@hotwired/stimulus";
import { DomEvents } from "../../../Shared/Generated/DomEvents";
import type { PersistentAudioMedia } from "../../../Shared/PersistentAudioPlayer/PersistentAudioPlayerUi";
import { formatTranslation, webTranslations } from "../../../Shared/webTranslations";
import { smartGuideLocationBroker } from "../../../Shared/BrowserLocation/SmartGuideLocationBroker";

type PoiDetailModel = {
    poiId: string;
    canonicalUrl: string;
    tourId?: string | null;
    isTourPoi: boolean;
    cmsSessionUrl: string;
    cmsSessionCheckEnabled: boolean;
    showCmsButtonWithoutSessionCheck: boolean;
};

export default class PoiDetailController extends Controller<HTMLElement> {
    static values = {
        model: Object
    };

    declare readonly modelValue: PoiDetailModel;

    private previouslyFocusedElement: HTMLElement | null = null;
    private readonly handleKeyDown = (event: KeyboardEvent): void => {
        const dialog = this.navigationDialog;
        if (!dialog || dialog.hidden) {
            return;
        }

        if (event.key === "Escape") {
            event.preventDefault();
            this.closeNavigationDialogInternal();
            return;
        }

        if (event.key === "Tab") {
            this.keepFocusInsideNavigationDialog(event);
        }
    };

    connect(): void {
        document.addEventListener("keydown", this.handleKeyDown);
        void this.updateCmsButtonVisibility();
    }

    disconnect(): void {
        document.removeEventListener("keydown", this.handleKeyDown);
        document.body.classList.remove("poi-navigation-dialog-open");
    }

    toggleDescription(event: Event): void {
        event.preventDefault();
        const button = event.currentTarget as HTMLButtonElement;
        const expanded = button.getAttribute("aria-expanded") === "true";
        this.element.classList.toggle("poi-detail--description-expanded", !expanded);
        button.setAttribute("aria-expanded", String(!expanded));
        button.textContent = expanded ? webTranslations.readMore : webTranslations.readLess;
    }

    scrollToMap(event: Event): void {
        event.preventDefault();
        const map = this.element.querySelector<HTMLElement>("[data-poi-map-preview]");
        map?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    openNavigationDialog(event: Event): void {
        event.preventDefault();
        this.openNavigationDialogInternal({ showPlanTourAction: false });
    }

    openAddressNavigationDialog(event: Event): void {
        event.preventDefault();
        this.openNavigationDialogInternal({ showPlanTourAction: true });
    }

    private openNavigationDialogInternal(options: { showPlanTourAction: boolean }): void {
        const dialog = this.navigationDialog;
        if (!dialog) {
            return;
        }

        const planTourAction = this.element.querySelector<HTMLElement>("[data-poi-navigation-plan-tour]");
        if (planTourAction) {
            planTourAction.hidden = !options.showPlanTourAction;
        }

        this.previouslyFocusedElement = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        dialog.hidden = false;
        dialog.setAttribute("aria-hidden", "false");
        document.body.classList.add("poi-navigation-dialog-open");
        this.updateNavigationDistance();

        requestAnimationFrame(() => {
            const initialFocus = this.element.querySelector<HTMLElement>("[data-poi-navigation-dialog-initial-focus]");
            const panel = this.navigationDialogPanel;
            (initialFocus ?? panel)?.focus();
        });
    }

    closeNavigationDialog(event: Event): void {
        event.preventDefault();
        this.closeNavigationDialogInternal();
    }

    closeNavigationDialogAfterTouchMapOpen(event: PointerEvent): void {
        if (event.pointerType !== "touch" && event.pointerType !== "pen") {
            return;
        }

        this.closeNavigationDialogInternal({ restoreFocus: false });
    }

    closeNavigationDialogFromBackdrop(event: MouseEvent): void {
        if (event.target === event.currentTarget) {
            this.closeNavigationDialogInternal();
        }
    }

    showOnMapFromDialog(event: Event): void {
        event.preventDefault();
        this.closeNavigationDialogInternal({ restoreFocus: false });
        const map = this.element.querySelector<HTMLElement>("[data-poi-map-preview]");
        map?.scrollIntoView({ behavior: "smooth", block: "start" });
        map?.focus({ preventScroll: true });
    }

    openMapApp(event: Event): void {
        const link = event.currentTarget as HTMLAnchorElement | null;
        if (!link || !this.isMobileBrowser()) {
            return;
        }

        const url = this.isIosBrowser()
            ? link.dataset.mapAppIosUrl
            : link.dataset.mapAppAndroidUrl;
        const fallbackUrl = link.dataset.mapAppDesktopUrl || link.href;

        if (!url) {
            return;
        }

        event.preventDefault();
        if (this.isWebUrl(url)) {
            this.openUrlInNewTab(url);
            return;
        }

        if (this.isAndroidBrowser()) {
            this.openUrlInNewTab(this.androidIntentUrl(url, fallbackUrl));
            return;
        }

        window.location.href = url;
    }

    playAudio(event: Event): void {
        const button = event.currentTarget as HTMLElement | null;
        if (!button) {
            return;
        }

        const media: PersistentAudioMedia = {
            id: button.dataset.audioId ?? "",
            url: button.dataset.audioUrl ?? "",
            title: button.dataset.audioTitle ?? "",
            artist: button.dataset.audioArtist ?? "SmartGuide",
            artworkUrl: button.dataset.audioArtworkUrl || undefined,
            detailUrl: button.dataset.audioDetailUrl ?? window.location.pathname
        };

        document.dispatchEvent(new CustomEvent(DomEvents.persistentAudioPlayRequested, {
            bubbles: true,
            detail: media
        }));
    }

    private async updateCmsButtonVisibility(): Promise<void> {
        const button = this.element.querySelector<HTMLAnchorElement>("[data-poi-cms-button]");
        if (!button) {
            return;
        }

        button.hidden = true;
        if (!this.modelValue.cmsSessionCheckEnabled) {
            return;
        }

        if (this.modelValue.showCmsButtonWithoutSessionCheck) {
            button.hidden = false;
            return;
        }

        try {
            const response = await fetch(this.modelValue.cmsSessionUrl, {
                credentials: "include",
                cache: "no-store"
            });
            if (!response.ok) {
                return;
            }

            const session = await response.json() as { isAuthenticated?: boolean };
            if (session.isAuthenticated === true) {
                button.hidden = false;
            }
        } catch {
            // Keep the CMS action hidden when the session cannot be confirmed.
        }
    }

    private get navigationDialog(): HTMLElement | null {
        return this.element.querySelector<HTMLElement>("[data-poi-navigation-dialog]");
    }

    private get navigationDialogPanel(): HTMLElement | null {
        return this.element.querySelector<HTMLElement>("[data-poi-navigation-dialog-panel]");
    }

    private closeNavigationDialogInternal(options: { restoreFocus: boolean } = { restoreFocus: true }): void {
        const dialog = this.navigationDialog;
        if (!dialog) {
            return;
        }

        dialog.hidden = true;
        dialog.setAttribute("aria-hidden", "true");
        document.body.classList.remove("poi-navigation-dialog-open");

        if (options.restoreFocus && this.previouslyFocusedElement?.isConnected) {
            this.previouslyFocusedElement.focus();
        }

        this.previouslyFocusedElement = null;
    }

    private keepFocusInsideNavigationDialog(event: KeyboardEvent): void {
        const panel = this.navigationDialogPanel;
        if (!panel) {
            return;
        }

        const focusableElements = Array.from(panel.querySelectorAll<HTMLElement>(
            "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
        )).filter(element => element.offsetParent !== null || element === document.activeElement);

        if (focusableElements.length === 0) {
            event.preventDefault();
            panel.focus();
            return;
        }

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    private updateNavigationDistance(): void {
        const distanceElement = this.element.querySelector<HTMLElement>("[data-poi-navigation-distance]");
        const navigationButton = this.element.querySelector<HTMLElement>("[data-poi-navigation-button]");
        const latitude = Number(navigationButton?.dataset.poiLatitude);
        const longitude = Number(navigationButton?.dataset.poiLongitude);

        if (!distanceElement || Number.isNaN(latitude) || Number.isNaN(longitude)) {
            return;
        }

        void smartGuideLocationBroker.requestFix("poi-navigation-distance", {
            maxAgeMs: 60_000,
            profile: "balanced"
        }).then(location => {
            distanceElement.textContent = formatTranslation(webTranslations.distanceFromYouFormat, this.formatDistance(this.distanceInMeters(
                location.latitude,
                location.longitude,
                latitude,
                longitude
            )));
        }).catch(() => { /* Distance remains unavailable after denial or timeout. */ });
    }

    private distanceInMeters(fromLatitude: number, fromLongitude: number, toLatitude: number, toLongitude: number): number {
        const earthRadiusMeters = 6_371_000;
        const latitudeDelta = this.toRadians(toLatitude - fromLatitude);
        const longitudeDelta = this.toRadians(toLongitude - fromLongitude);
        const fromLatitudeRadians = this.toRadians(fromLatitude);
        const toLatitudeRadians = this.toRadians(toLatitude);
        const halfChordLength = Math.sin(latitudeDelta / 2) ** 2 +
            Math.cos(fromLatitudeRadians) * Math.cos(toLatitudeRadians) * Math.sin(longitudeDelta / 2) ** 2;

        return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(halfChordLength), Math.sqrt(1 - halfChordLength));
    }

    private toRadians(value: number): number {
        return value * Math.PI / 180;
    }

    private formatDistance(meters: number): string {
        if (meters < 1_000) {
            return `${Math.max(1, Math.round(meters))} m`;
        }

        if (meters < 10_000) {
            return `${(meters / 1_000).toFixed(1)} km`;
        }

        return `${Math.round(meters / 1_000)} km`;
    }

    private openUrlInNewTab(url: string): void {
        const externalWindow = window.open(url, "_blank", "noopener,noreferrer");
        if (externalWindow) {
            try {
                externalWindow.opener = null;
            } catch {
            }
        }
    }

    private isWebUrl(url: string): boolean {
        try {
            const parsedUrl = new URL(url, window.location.href);
            return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
        } catch {
            return false;
        }
    }

    private androidIntentUrl(appUrl: string, fallbackUrl: string): string {
        try {
            const parsedUrl = new URL(appUrl);
            const scheme = parsedUrl.protocol.replace(":", "");
            const intentPath = `${parsedUrl.host}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
            const fallbackPart = this.isWebUrl(fallbackUrl)
                ? `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};`
                : "";

            return `intent://${intentPath}#Intent;scheme=${scheme};${fallbackPart}end`;
        } catch {
            return appUrl;
        }
    }

    private isAndroidBrowser(): boolean {
        return /Android/i.test(navigator.userAgent);
    }

    private isMobileBrowser(): boolean {
        return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
            window.matchMedia?.("(pointer: coarse)").matches === true;
    }

    private isIosBrowser(): boolean {
        return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
            (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
    }
}
